import { useAtom, useAtomValue } from "jotai";
import { colors } from "theme";

import Button from "components/Button";
import Flex from "components/Flex";
import { CameraIcon } from "components/Icons/Camera";
import PageLayout from "components/PageLayout";
import PhotoGrid from "components/PhotoGrid";
import PhotoUpload from "components/PhotoUpload";
import { Photo } from "components/PhotoUpload/types";
import Text from "components/Text";
import TextInput from "components/TextInput";

import { clientConfigAtom } from "atoms/clientConfig";
import { mileagePhotoAtom } from "atoms/vehicleCondition/mileage";
import { useCheckpoint } from "hooks/useCheckpoint";
import { useClientTranslation } from "hooks/useClientTranslation";
import { usePageRouting } from "hooks/usePageRouting";
import { Checkpoint } from "types/Checkpoint";
import { processImageForUpload } from "utils/imageProcessing/processImageForUpload";

import { VehicleConditionQuestion } from "./VehicleConditionQuestion";
import { useVehicleConditionForm } from "./hooks/useVehicleConditionForm";
import { getUserLocale } from "utils/locale/userLocale";

export const VehicleCondition = () => {
  const targetReached = useCheckpoint(Checkpoint.CLAIM_SUBMITTED);
  const { onTrackAndNext, currentProgressStep, continueCTA } = usePageRouting();
  const { t } = useClientTranslation();

  const {
    locale,
    vehicleCondition: { showMileage, showMileagePhoto, questions },
  } = useAtomValue(clientConfigAtom);

  const { handleSubmit, onType, isValid, warning, mileage } = useVehicleConditionForm(getUserLocale(locale));

  const [mileagePhoto, setMileagePhoto] = useAtom(mileagePhotoAtom);

  const onDrop = async (files: File[]) => {
    const [image] = files;
    const processedImage = await processImageForUpload({
      file: image,
      alt: "Mileage photo to upload",
      type: Photo.Mileage,
    });

    setMileagePhoto([processedImage]);
  };

  return (
    <PageLayout
      as="form"
      onSubmit={handleSubmit(onTrackAndNext({ name: "Vehicle condition", properties: { mileage } }))}
      currentStep={currentProgressStep}
      title={t("vehicle_condition.header")}
      dataTestId="vehicleCondition"
      forceContinue={targetReached}
      bottomButtons={
        <Button disabled={!isValid} fullWidth type="submit">
          {continueCTA}
        </Button>
      }
    >
      <Flex py={16} px={24} gap={16}>
        <Flex gap={48}>
          {questions.map((variant) => (
            <VehicleConditionQuestion key={variant} variant={variant} />
          ))}

          {showMileage && (
            <Flex gap={24}>
              <TextInput
                name="mileage"
                value={mileage}
                label={<Text sizeVariant="lg">{t("vehicle_condition.mileage.question")}</Text>}
                onChange={onType}
                warning={warning}
                rightSection={
                  <Text sizeVariant="md" mr={16} style={{ color: colors.grey[50] }}>
                    {t("vehicle_condition.mileage.unit")}
                  </Text>
                }
              />
              {showMileagePhoto &&
                (mileagePhoto.length === 1 ? (
                  <PhotoGrid images={mileagePhoto} deleteFromAtom={setMileagePhoto} />
                ) : (
                  <PhotoUpload onDrop={onDrop} multiple={false}>
                    <Button typeVariant="secondary" leftIcon={<CameraIcon />}>
                      {t("vehicle_condition.mileage.add_photo")}
                    </Button>
                  </PhotoUpload>
                ))}
            </Flex>
          )}
        </Flex>
      </Flex>
    </PageLayout>
  );
};
